import React, { useMemo } from "react";
import { Row, Col, Button, ListGroup, Form } from "react-bootstrap";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import { createElementIdFactory, createSLMVolumeFoliosListElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/List.css";
import "containers/common/Table.css";

const DATA_SET = "volumeFolios";
const ENDPOINT = "slmVolumeFolios";
const PRIMARY_KEY = "volume_folio_id";

function formatDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function formatVolumeFolio(row) {
  if (row.volume && row.folio) {
    return `V${row.volume} F${row.folio}`;
  }
  return row.other || "";
}

export default function SLMVolumeFoliosList({
  filter,
  data = [],
  dataFetch,
  isLoading = false,
  isAuthenticated,
  controlType = "list",
  saveRecord,
  updateTableState,
  context,
  ctx,
  listAmount,
  user,
  validateRelatedRecord,
}) {
  const rows = Array.isArray(data) ? data : [];
  const createId = useMemo(() => createElementIdFactory("slm-volume-folios-list"), []);
  const elementIds = useMemo(() => createSLMVolumeFoliosListElementIds(createId), [createId]);
  const columnHelper = useMemo(() => createColumnHelper(), []);

  const foreignKey = useMemo(() => {
    const activeContext = (context || ctx || "").toLowerCase();
    if (activeContext === "slmlease") {
      return { key: "lease_id", value: filter?.leaseid ?? null };
    }
    if (activeContext === "slmsale") {
      return { key: "sale_id", value: filter?.saleid ?? null };
    }

    if (filter?.leaseid || filter?.lease_id) {
      return { key: "lease_id", value: filter?.leaseid ?? filter?.lease_id ?? null };
    }

    if (filter?.saleid || filter?.sale_id) {
      return { key: "sale_id", value: filter?.saleid ?? filter?.sale_id ?? null };
    }

    const pathname = window?.location?.pathname?.toLowerCase() || "";
    if (pathname.includes("/slmleases/")) {
      return { key: "lease_id", value: null };
    }

    if (pathname.includes("/slmsales/")) {
      return { key: "sale_id", value: null };
    }

    return { key: "property_id", value: filter?.propertyid ?? null };
  }, [context, ctx, filter]);

  const syncRows = (nextRows) => {
    updateTableState?.(nextRows, DATA_SET);
  };

  const sortRowsForDisplay = (inputRows) => {
    const nextRows = Array.isArray(inputRows) ? [...inputRows] : [];

    const asNumber = (value) => {
      const parsed = Number.parseInt(value, 10);
      return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
    };

    const asText = (value) => String(value || "").trim().toLowerCase();

    nextRows.sort((left, right) => {
      const titleCompare = asText(left.title_reference).localeCompare(asText(right.title_reference));
      if (titleCompare !== 0) {
        return titleCompare;
      }

      const volumeCompare = asNumber(left.volume) - asNumber(right.volume);
      if (volumeCompare !== 0) {
        return volumeCompare;
      }

      const folioCompare = asNumber(left.folio) - asNumber(right.folio);
      if (folioCompare !== 0) {
        return folioCompare;
      }

      const otherCompare = asText(left.other).localeCompare(asText(right.other));
      if (otherCompare !== 0) {
        return otherCompare;
      }

      return (left.volume_folio_id || 0) - (right.volume_folio_id || 0);
    });

    return nextRows;
  };

  const refreshSort = () => {
    syncRows(sortRowsForDisplay(rows));
  };

  const normalizeValidationResult = (result, fallbackMessage) => {
    if (Array.isArray(result)) {
      const [isValid = true, validationMessage = ""] = result;
      return { isValid: Boolean(isValid), message: validationMessage || fallbackMessage || "Invalid record." };
    }

    if (result && typeof result === "object") {
      const isValid = result.isValid !== undefined ? Boolean(result.isValid) : true;
      const message = result.validationMessage || result.message || fallbackMessage || "Invalid record.";
      return { isValid, message };
    }

    if (typeof result === "boolean") {
      return { isValid: result, message: fallbackMessage || "Invalid record." };
    }

    return { isValid: true, message: "" };
  };

  const runRelatedValidation = (record) => {
    if (typeof validateRelatedRecord !== "function") {
      return { isValid: true, message: "" };
    }

    return normalizeValidationResult(validateRelatedRecord(record, ENDPOINT), "Volume folio validation failed.");
  };

  const persistRows = async (nextRows, rowIndex) => {
    const record = nextRows[rowIndex];
    if (!record || !record.modified) {
      return;
    }

    const response = await saveRecord?.(record, ENDPOINT, PRIMARY_KEY);
    if (response?.result === false) {
      window.alert(response.error_message || "Unable to save volume folio record.");
      return;
    }

    const savedRows = [...nextRows];
    savedRows[rowIndex] = {
      ...savedRows[rowIndex],
      modified: false,
      new_record: false,
      [PRIMARY_KEY]: response?.id ?? savedRows[rowIndex][PRIMARY_KEY],
    };

    syncRows(savedRows);
    await dataFetch?.();
  };

  const updateCell = (rowIndex, field, value) => {
    const nextRows = [...rows];
    nextRows[rowIndex] = {
      ...(nextRows[rowIndex] || {}),
      [field]: value,
      modified: true,
      last_edited_by: user,
    };
    syncRows(nextRows);
  };

  const commitCell = async (rowIndex, field, value) => {
    const nextRows = [...rows];
    const updatedRecord = {
      ...(nextRows[rowIndex] || {}),
      [field]: value,
      modified: true,
      last_edited_by: user,
    };

    const validation = runRelatedValidation(updatedRecord);
    if (!validation.isValid) {
      window.alert(validation.message);
      return;
    }

    nextRows[rowIndex] = updatedRecord;
    syncRows(nextRows);
    await persistRows(nextRows, rowIndex);
  };

  const addRecord = () => {
    const nextRows = [
      {
        [PRIMARY_KEY]: null,
        [foreignKey.key]: foreignKey.value,
        title_reference: null,
        volume: null,
        folio: null,
        other: null,
        created_by: user,
        last_edited_by: user,
        new_record: true,
        modified: true,
      },
      ...rows,
    ];

    syncRows(nextRows);
  };

  const copyRecord = (rowIndex) => {
    const source = rows[rowIndex];
    if (!source) {
      return;
    }

    const requestedCopies = Number.parseInt(window.prompt("How many copies do you want to create?", "1"), 10);
    if (!requestedCopies || requestedCopies < 1) {
      return;
    }

    const copies = Array.from({ length: requestedCopies }, (_, copyIndex) => {
      const parsedFolio = Number.parseInt(source.folio, 10);
      return {
        ...source,
        [PRIMARY_KEY]: null,
        folio: Number.isNaN(parsedFolio) ? source.folio : String(parsedFolio + copyIndex + 1),
        created_by: user,
        last_edited_by: user,
        new_record: true,
        modified: true,
      };
    });

    const nextRows = [...rows];
    nextRows.splice(rowIndex + 1, 0, ...copies);
    syncRows(nextRows);
  };

  const deleteRow = async (rowIndex) => {
    const row = rows[rowIndex];
    if (!row) {
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this volume folio?");
    if (!confirmed) {
      return;
    }

    if (row[PRIMARY_KEY]) {
      const response = await saveRecord?.(
        {
          ...row,
          archived_date: new Date().toISOString(),
          modified: true,
          last_edited_by: user,
        },
        ENDPOINT,
        PRIMARY_KEY
      );

      if (response?.result === false) {
        window.alert(response.error_message || "Unable to delete volume folio record.");
        return;
      }
    }

    syncRows(rows.filter((_, index) => index !== rowIndex));
    await dataFetch?.();
  };

  const editableCell = (field, placeholder = "") => (info) => (
    <Form.Control
      size="sm"
      value={info.getValue() ?? ""}
      placeholder={placeholder}
      onChange={(event) => updateCell(info.row.index, field, event.target.value)}
      onBlur={(event) => commitCell(info.row.index, field, event.target.value)}
    />
  );

  const readOnlyCell = () => (info) => (
    <Form.Control
      size="sm"
      value={info.getValue() ?? ""}
      readOnly
      disabled
    />
  );

  const columns = [
    columnHelper.accessor("volume_folio_id", { id: "volume_folio_id", header: "ID", size: 70 }),
    columnHelper.accessor("title_reference", { id: "title_reference", header: "Title Reference", cell: readOnlyCell(), size: 180 }),
    columnHelper.accessor("volume", { id: "volume", header: "Volume", cell: editableCell("volume"), size: 100 }),
    columnHelper.accessor("folio", { id: "folio", header: "Folio", cell: editableCell("folio"), size: 100 }),
    columnHelper.accessor("other", { id: "other", header: "Other", cell: editableCell("other"), size: 180, meta: { wrap: true } }),
    columnHelper.accessor("created_date", { id: "created_date", header: "Created Date", cell: (info) => formatDate(info.getValue()), size: 100 }),
    columnHelper.accessor("created_by", { id: "created_by", header: "Created By", size: 100 }),
    columnHelper.accessor("last_edited_date", { id: "last_edited_date", header: "Last Edited Date", cell: (info) => formatDate(info.getValue()), size: 100 }),
    columnHelper.accessor("last_edited_by", { id: "last_edited_by", header: "Last Edited By", size: 100 }),
    columnHelper.display({
      id: "delete",
      header: "",
      size: 80,
      cell: (info) => (
        <Button type="button" size="sm" variant="danger" onClick={() => deleteRow(info.row.index)}>
          Delete
        </Button>
      ),
    }),
    columnHelper.display({
      id: "copy",
      header: "",
      size: 80,
      cell: (info) => (
        <Button type="button" size="sm" variant="primary" onClick={() => copyRecord(info.row.index)}>
          Copy
        </Button>
      ),
    }),
  ];

  const renderList = () => {
    const limit = Number.parseInt(listAmount, 10) || 5;
    return rows.slice(0, limit).map((row, index) => (
      <ListGroup.Item key={row.volume_folio_id ?? `volume-folio-${index}`}>
        {row.title_reference || "Untitled"}
        <br />
        {formatVolumeFolio(row)}
      </ListGroup.Item>
    ));
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="SLMVolumeFoliosSection" id={elementIds.container}>
      {controlType === "table" && (
        <Row className="show-Container g-2 mb-2">
          <Col xs={12} className="text-right d-flex justify-content-end gap-2">
            <Button id={elementIds.refreshSortButton} variant="secondary" size="sm" onClick={refreshSort}>
              Refresh Sort
            </Button>
            <Button id={elementIds.addButton} variant="primary" size="sm" onClick={addRecord}>
              Add
            </Button>
          </Col>
        </Row>
      )}

      <Row className="show-Container g-2">
        <Col xs={12}>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader cssOverride={{ display: "block", margin: "0 auto" }} size={20} color="#e03c31" loading={isLoading} />
              </div>
            </div>
          ) : controlType === "table" ? (
            <GenericTable
              columns={columns}
              data={rows}
              pageSize={PAGE_SIZES.slmVolumeFolioList}
              enablePagination={true}
              hasHeaderSorting={true}
              section="slm-volume-folios-list"
              elementIds={elementIds.table}
              containerClassName="componentRendering"
              enforceMinRowsHeight={true}
            />
          ) : (
            <ListGroup>{renderList()}</ListGroup>
          )}
        </Col>
      </Row>
    </div>
  );
}
